import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, ModalError } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { fetchUstawieniaWyspy, zapiszUstawienie, type UstawienieWyspy } from '@/lib/ustawienia-api';
import { cn } from '@/lib/utils';

/** Parametr `SetupData`, od którego SPSUI w ogóle pokazuje kafelki grup (`BOGraphicalSelect.GraphicSelectEnabled`). */
export const PARAM_KAFELKOW = 'GRAPHIC_SELECT_ENABLED';

/**
 * Wartość tak/nie TAK, JAK CZYTA JĄ SPS: `GetBooleanSetting` = `int.TryParse(...) != 0`, więc „1"
 * i „-1" włączają, a pusty napis albo cokolwiek nie-liczbowego wyłącza.
 */
export function czyWlaczone(wartosc: string | null): boolean {
  const t = (wartosc ?? '').trim();
  return /^[+-]?\d+$/.test(t) && Number(t) !== 0;
}

/**
 * Które automaty zmienić, żeby wyszło `zaznaczone` — osobno do włączenia i do wyłączenia.
 * Automat, który już ma żądany stan, nie trafia nigdzie: serwis i tak odmówiłby zapisu bez zmiany.
 */
export function zmianyKafelkow(
  wartosci: readonly { vmid: number; wartosc: string | null }[],
  zaznaczone: ReadonlySet<number>,
): { wlacz: number[]; wylacz: number[] } {
  const wlacz: number[] = [];
  const wylacz: number[] = [];
  for (const w of wartosci) {
    const teraz = czyWlaczone(w.wartosc);
    const ma = zaznaczone.has(w.vmid);
    if (ma && !teraz) wlacz.push(w.vmid);
    if (!ma && teraz) wylacz.push(w.vmid);
  }
  return { wlacz, wylacz };
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  WYBÓR PO OBRAZKACH NA AUTOMACIE — `GRAPHIC_SELECT_ENABLED` na ekranie grup artykułów.
 *
 *  ⚠️⚠️ ZAMÓWIONE 27.10.2026 razem z obrazkiem grupy: „dodaj ten jeden parametr z setup_data
 *  w zakładce z grupami, żeby nie trzeba było gdzieś szukać w configu". Bez niego kafelków grup
 *  w SPSUI nie ma wcale, więc obrazek grupy bez tego przełącznika byłby niewidoczny.
 *
 *  ⚠️⚠️ PRZEBUDOWANE TEGO SAMEGO DNIA po pierwszym obejrzeniu: kafelek automatu klikany wprost
 *  kolejkował zapis po `confirm()` przeglądarki („generic okienko" — a `confirm()` jest w portalu
 *  zakazany od 02.09.2026, uzasadnienie w `OknoPotwierdzenia`), a rząd zielonych kafelków „dziwnie
 *  wyglądał". Teraz w wierszu jest sam STAN, a zmiana idzie przez okno z listą automatów: zaznacza
 *  się, gdzie ma być włączone, i dopiero „Zapisz" coś kolejkuje. Okno jest zarazem potwierdzeniem.
 *
 *  ⚠️ ZAPIS IDZIE TĄ SAMĄ DROGĄ CO ZAKŁADKA „USTAWIENIA" (`/ustawienia`): walidacja z SPSConfig,
 *  kolejka zmian, próg wersji agenta. Tu jest tylko krótszy dostęp do jednego parametru.
 *
 *  ⚠️ SPSUI CZYTA `SetupData` RAZ, PRZY STARCIE (`ApplicationSettings.Initialise`, zdekompilowane),
 *  i tak samo raz buduje kafelki — stąd dopisek o restarcie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function KafelkiGrupSps({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const [edycja, setEdycja] = useState(false);

  const q = useQuery({
    queryKey: ['ustawienia-wyspy', groupId, PARAM_KAFELKOW],
    queryFn: () => fetchUstawieniaWyspy(groupId, PARAM_KAFELKOW),
  });

  if (q.isPending || q.isError) return null;
  const u = q.data.ustawienia[0];
  const nazwa = (vmid: number) => q.data.automaty.find((a) => a.vmid === vmid)?.nazwa ?? `VMID ${vmid}`;

  const wlaczonych = u?.wartosci.filter((w) => czyWlaczone(w.wartosc)).length ?? 0;
  const razem = u?.wartosci.length ?? 0;
  const wKolejce = u?.wartosci.some((w) => w.wKolejce) ?? false;
  const stan =
    wlaczonych === razem ? t('igrp.kafelki.wszedzie') : wlaczonych === 0 ? t('igrp.kafelki.nigdzie') : t('igrp.kafelki.czesc');

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-card px-3 py-2 text-sm">
      <LayoutGrid className="size-4 shrink-0 text-muted-foreground" />
      <span className="font-medium">{t('igrp.kafelki.tytul')}</span>
      <span className="font-mono text-[11px] text-muted-foreground">{PARAM_KAFELKOW}</span>

      {u === undefined ? (
        <span className="text-xs text-muted-foreground">{t('igrp.kafelki.brak')}</span>
      ) : (
        <>
          <span
            className={cn('tabular-nums', wlaczonych > 0 && wlaczonych < razem && 'text-[var(--warning)]')}
            /** Pełna lista w dymku — w wierszu zostaje sama liczba. */
            title={u.wartosci.map((w) => `${nazwa(w.vmid)}: ${czyWlaczone(w.wartosc) ? t('ust.tak') : t('ust.nie')}`).join('\n')}
          >
            {stan.replace('{n}', String(wlaczonych)).replace('{m}', String(razem))}
          </span>
          {wKolejce && <Badge variant="neutral">{t('igrp.kafelki.wKolejce')}</Badge>}
          <Button type="button" size="sm" variant="outline" className="ml-auto" onClick={() => setEdycja(true)}>
            {t('igrp.kafelki.zmien')}
          </Button>
        </>
      )}

      {edycja && u !== undefined && (
        <OknoKafelkow
          groupId={groupId}
          ustawienie={u}
          nazwa={nazwa}
          zapis={q.data.zapis}
          onClose={() => setEdycja(false)}
        />
      )}
    </div>
  );
}

function OknoKafelkow({
  groupId,
  ustawienie,
  nazwa,
  zapis,
  onClose,
}: {
  groupId: string;
  ustawienie: UstawienieWyspy;
  nazwa: (vmid: number) => string;
  zapis: { mozliwy: boolean; wymagana: string; starsze: string[] };
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [zaznaczone, setZaznaczone] = useState<Set<number>>(
    () => new Set(ustawienie.wartosci.filter((w) => czyWlaczone(w.wartosc)).map((w) => w.vmid)),
  );
  const [blad, setBlad] = useState<string | null>(null);

  const { wlacz, wylacz } = zmianyKafelkow(ustawienie.wartosci, zaznaczone);
  const bezZmian = wlacz.length === 0 && wylacz.length === 0;

  /**
   * ⚠️ DWIE OPERACJE, gdy jedne automaty się włącza, a inne wyłącza — zapis jednego parametru niesie
   * jedną wartość. Każda jest osobnym wpisem w „Zmianach w toku", z listą automatów w tytule.
   */
  const zapisz = useMutation({
    mutationFn: async () => {
      let automatow = 0;
      if (wlacz.length > 0) automatow += (await zapiszUstawienie(groupId, PARAM_KAFELKOW, '1', { vmids: wlacz })).automatow;
      if (wylacz.length > 0) automatow += (await zapiszUstawienie(groupId, PARAM_KAFELKOW, '0', { vmids: wylacz })).automatow;
      return automatow;
    },
    onSuccess: (n) => {
      toast.success(t('igrp.kafelki.zapisano').replace('{n}', String(n)));
      void qc.invalidateQueries({ queryKey: ['ustawienia-wyspy', groupId] });
      onClose();
    },
    onError: (e: Error) => {
      setBlad(e instanceof ApiError ? e.message : t('common.error'));
      void qc.invalidateQueries({ queryKey: ['ustawienia-wyspy', groupId] });
    },
  });

  const przelacz = (vmid: number) =>
    setZaznaczone((prev) => {
      const next = new Set(prev);
      if (next.has(vmid)) next.delete(vmid);
      else next.add(vmid);
      return next;
    });

  return (
    <Dialog open onOpenChange={(v) => !v && !zapisz.isPending && onClose()}>
      <DialogContent title={t('igrp.kafelki.tytul')} description={<span className="font-mono text-xs">{PARAM_KAFELKOW}</span>}>
        <div className="space-y-3 text-sm">
          <ul className="divide-y divide-border rounded-md border border-border">
            {ustawienie.wartosci.map((w) => (
              <li key={w.vmid}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={zaznaczone.has(w.vmid)}
                    disabled={zapisz.isPending || w.wKolejce}
                    onChange={() => przelacz(w.vmid)}
                  />
                  <span className="min-w-0 flex-1 truncate">{nazwa(w.vmid)}</span>
                  <span className="font-mono text-[11px] text-muted-foreground">VMID {w.vmid}</span>
                  {w.wKolejce && <Badge variant="neutral">{t('igrp.kafelki.wKolejce')}</Badge>}
                </label>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">{t('igrp.kafelki.restart')}</p>
          {!zapis.mozliwy && (
            <p className="text-xs text-[var(--warning)]">
              {t('igrp.kafelki.starsze').replace('{wersja}', zapis.wymagana).replace('{wezly}', zapis.starsze.join(', '))}
            </p>
          )}
          <ModalError>{blad}</ModalError>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={zapisz.isPending} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={bezZmian || !zapis.mozliwy || zapisz.isPending} onClick={() => zapisz.mutate()}>
            {zapisz.isPending && <Loader2 className="animate-spin" />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
